import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { UpdateAgencyFormType, updateAgencySchema } from "../schema/schema";
import { zodResolver } from "@hookform/resolvers/zod";
import AgentAgencyForm from "./AgentAgencyForm";
import Typography from "@mui/joy/Typography";
import Stack from "@mui/joy/Stack";
import SearchComponent from "./SearchComponent";
import axiosInstance from "@/services/api/axiosInstance";
import { toast } from "sonner";
import StickyFooter from "@/components/StickyFooter/StickyFooter";
import Button from "@mui/joy/Button";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import LinkButton from "@/components/LinkButton/LinkButton";
import { ADMIN_ROUTE_PREFIX } from "@/types/constants/common";
import { AgencyData } from "../constants/types";

const UpdateAgencyForm = () => {
  const [agency, setAgency] = useState<AgencyData | null>(null);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  useEffect(() => {
    if (agency !== null) {
      const { Email, Phone, Address, SourceAgencyId, AgencyName } = agency;
      setValue("ttandEmail", Email ?? "");
      setValue("businessPhone", Phone?.replace(/-/g, "") ?? "");
      setValue("city", Address?.City ?? "");
      setValue("province", Address?.State ?? "");
      setValue("postalCode", Address?.Zip ?? "");
      setValue("streetAddress", Address?.Address1 ?? "");
      setValue("brandedAs", AgencyName ?? "");
      setValue("clientlinqBranch", SourceAgencyId);
      setValue("status", agency.IsActive);
    }
  }, [agency]);

  const {
    register,
    control,
    handleSubmit,
    setValue,
    setError,
    formState: { errors },
  } = useForm<UpdateAgencyFormType>({
    resolver: zodResolver(updateAgencySchema),
    defaultValues: {
      streetAddress: "",
      city: "",
      province: "",
      postalCode: "",
      ttandEmail: "",
      businessPhone: "",
      brandedAs: "",
      clientlinqBranch: "",
      status: false,
    },
  });

  const onSubmit = async (data: UpdateAgencyFormType) => {
    setIsSubmitting(true);
    try {
      // format updateAgency -> Constria
      const formatedData = {
        AgencyName: data.brandedAs,
        SourceAgencyId: parseInt(data.clientlinqBranch, 10),
        Address: {
          Address1: data.streetAddress,
          Address2: "",
          City: data.city,
          State: data.province,
          Zip: data.postalCode,
          Country: "CA",
        },
        Phone: data.businessPhone,
        Email: data.ttandEmail,
        Website: "",
        IsActive: data.status,
      };
      const result = await axiosInstance.put(`/admin/constria/update-agency`, formatedData);
      toast.success(result.data?.message || "Agency Updated Successfully");
    } catch (error: any) {
      toast.error(error?.response?.data?.message || "Something Went Wrong");
    } finally {
      setIsSubmitting(false);
    }
  };
  return (
    <Stack>
      <Typography
        level="h3"
        sx={{
          mb: 4,
        }}
      >
        {"Update Agency"}
      </Typography>
      <SearchComponent type={"agency"} onDataFetched={setAgency} mode={"update"} />
      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack direction={"row"} gap={4}>
          <AgentAgencyForm
            mode={"update"}
            type={"agency"}
            register={register}
            control={control}
            setValue={setValue}
            setError={setError}
            errors={errors}
            disable={!agency}
          />
        </Stack>
        <StickyFooter sx={{ mt: 4 }}>
          <Button
            type="submit"
            sx={{ textTransform: "capitalize" }}
            loading={isSubmitting}
            disabled={isSubmitting || !agency}
            startDecorator={<AddOutlinedIcon />}
          >
            Update Agency
          </Button>
          <LinkButton color="neutral" variant="outlined" to={`${ADMIN_ROUTE_PREFIX}/`} disabled={isSubmitting}>
            Cancel
          </LinkButton>
        </StickyFooter>
      </form>
    </Stack>
  );
};

export default UpdateAgencyForm;
